import React, { useContext, useEffect, useState } from 'react';
import { ContentChart } from '@meteor/frontend-core';
import { AppContext } from '../../../../contexts/AppContext';
import { GLOBAL_EVENT } from '../../../../consts/common/global-event';
import APIList from '../../../../http/ApiList';
import { cloneDeep } from 'lodash';
import ErrorBoundary from '../../../common/error-boundary';

const CustomizeDumpChart: React.FC<any> = (props) => {
  const { authInfo, echartTheme, globalEvent, setGlobalEvent } = useContext(AppContext);
  const { property } = props;
  const [chartLoading, setChartLoading] = useState<any>(false);
  const [chartData, setChartData] = useState<any>(null);
  const xData = ['2024/06', '2024/05', '2024/04', '2024/03', '2024/02', '2024/01'];
  const defaultChartOptions: any = {
    grid: {
      left: 40,
      right: 90,
      bottom: 60,
      top: 10,
    },
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        let str = `<table border="0"><div>${params[0].name}<br/>`;
        for (let index = 0; index < params.length; index++) {
          const item = params[index];
          str += `<tr><td>${item.marker}${item.seriesName}<td> `;
          str += `<td style="text-align: right">${item.value}<td>`;
        }
        str += `</table></div>`;
        return str;
      },
    },
    xAxis: {
      type: 'category',
      splitLine: {
        show: true,
      },
      axisLabel: {
        margin: 20,
        fontSize: 12,
        rotate: 25,
        align: 'center',
      },
      boundaryGap: false,
      data: xData,
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        marginRight: 20,
        fontSize: 12,
        formatter: (value) => {
          return value === 10 ? value + '+' : value;
        },
      },
      inverse: true,
      interval: 1,
      min: 1,
      max: 10,
    },
  };
  const [options, setOptions] = useState<any>(defaultChartOptions);
  const oneHundredMillion = 100000000;
  const colors = [
    '#464879',
    '#5d7db3',
    '#b6cad7',
    '#84709b',
    '#edca7f',
    '#e9b9aa',
    '#d98481',
    '#f47db5',
    '#df4c5b',
    '#989e6a',
    '#5d6e4a',
    '#926851',
  ];

  const scatterSeries = (data) => {
    let dataSeries: any[] = [];
    data.forEach((d, i) => {
      let seriesItem = {
        name: d[0],
        symbolSize: 10,
        type: 'line',
        smooth: true,
        emphasis: {
          focus: 'series',
        },
        endLabel: {
          show: true,
          formatter: '{a}',
          distance: 20,
        },
        lineStyle: {
          width: 2,
        },
        color: colors[i],
        datasetIndex: i,
      };
      dataSeries.push(seriesItem);
    });
    return dataSeries;
  };

  const getScatterData = (data) => {
    let dataSetArray: any[] = [];
    data.forEach((d: any[], i) => {
      let sourceArray = [];
      d.slice(1).forEach((d, i) => sourceArray.push([i, d > 10 ? 10 : d]));
      dataSetArray.push({ id: i, source: sourceArray });
    });
    return dataSetArray;
  };

  const getChartData = (event: string) => {
    APIList.getCustomizeLayoutData()
      .post({ email: authInfo?.user?.attributes?.email || '', property, event })
      .then((res: any) => {
        setChartData(res.data);
        setChartLoading(false);
      });
  };

  useEffect(() => {
    if (!property || !property.chartOptions || !property.chartOptions.options) {
      return;
    }
    try {
      let op;
      if (property.chartOptions.options) {
        const reviver = (key, value) => {
          const arrowFunctionRegex = /^\s*\(?\s*([^)]*)\s*\)?\s*=>\s*{[^]*}$/;
          if (typeof value === 'string' && (value.startsWith('function') || arrowFunctionRegex.test(value))) {
            return new Function('return ' + value)();
          }
          return value;
        };
        const tempOp = JSON.parse(property.chartOptions.options, reviver);
        op = {
          ...options,
          ...tempOp,
        };
      } else {
        op = defaultChartOptions;
      }
      setOptions(cloneDeep({ ...op, echartTheme }));
    } catch (error) {}
  }, [property]);

  useEffect(() => {
    if (!globalEvent) {
      return;
    }
    if (
      (property.dataSource &&
        property.dataSource.triggerEvent &&
        property.dataSource.triggerEvent.find((triggerEvent) => {
          return triggerEvent.event === globalEvent.eventType;
        })) ||
      globalEvent.eventType === GLOBAL_EVENT.PAGE_INIT
    ) {
      setChartLoading(true);
      setTimeout(() => {
        getChartData(globalEvent);
      }, 500);
    }
  }, [globalEvent]);

  useEffect(() => {
    if (!chartData || chartData.length === 0) {
      return;
    }
    options.series = scatterSeries(chartData);
    options.dataset = getScatterData(chartData);
    setOptions(cloneDeep({ ...options, echartTheme }));
  }, [chartData]);

  return (
    <ErrorBoundary>
      <ContentChart
        title={{
          value: (
            <>
              <span>{property.title}</span> <span style={{ float: 'right' }}>{property.subtitle}</span>
            </>
          ),
          styles: {},
        }}
        contentList={[]}
        chartOptions={options}
        showSeriesLabel={false}
        mouseWheelZoomLock={false}
        loading={chartLoading}
        height={'90%'}
        tooltips={['zoomIn']}
        isBank={!chartData || chartData.length === 0}
        onChartEvent={setGlobalEvent}
      />
    </ErrorBoundary>
  );
};

export default CustomizeDumpChart;
